import type { OutputData } from '@editorjs/editorjs';
import edjsHTML from 'editorjs-html';

/**
 * Configuración de parsers personalizados para convertir bloques de Editor.js a HTML
 */
const getCustomParsers = () => ({
  title: (block: any) => {
    const level = block.data.titleType || 'H1';
    const text = block.data.text || '';
    const color = block.data.color ? ` style="color: ${block.data.color}"` : '';
    const alignment = block.data.alignText || '';
    const alignClass = alignment ? ` class="${alignment}"` : '';
    return `<${level.toLowerCase()}${color}${alignClass}>${text}</${level.toLowerCase()}>`;
  },
  paragraph: (block: any) => {
    return `<p>${block.data.text || ''}</p>`;
  },
  header: (block: any) => {
    const level = block.data.level || 1;
    return `<h${level}>${block.data.text || ''}</h${level}>`;
  },
  list: (block: any) => {
    const style = block.data.style;

    // Función recursiva para procesar items anidados
    const processItems = (items: any[], isChecklist: boolean = false): string => {
      return items.map((item: any) => {
        // Si el item es un string (formato legacy), usarlo directamente
        if (typeof item === 'string') {
          return `<li>${item}</li>`;
        }

        // Si el item es un objeto con la nueva estructura
        if (typeof item === 'object' && item.content !== undefined) {
          let listItem = '';

          if (isChecklist) {
            // Para checklist, usar checkbox HTML
            const checked = item.meta?.checked ? 'checked' : '';
            listItem = `<li><input type="checkbox" ${checked} disabled> ${item.content}`;
          } else {
            listItem = `<li>${item.content}`;
          }

          // Si hay items anidados, procesarlos recursivamente
          if (item.items && item.items.length > 0) {
            const nestedIsChecklist = style === 'checklist';
            const nestedStyle = style === 'ordered' ? 'ol' : 'ul';
            const nestedItems = processItems(item.items, nestedIsChecklist);

            if (nestedIsChecklist) {
              listItem += `<ul>${nestedItems}</ul>`;
            } else {
              listItem += `<${nestedStyle}>${nestedItems}</${nestedStyle}>`;
            }
          }

          listItem += '</li>';
          return listItem;
        }

        // Fallback para casos no esperados
        return `<li>${String(item)}</li>`;
      }).join('');
    };

    // Determinar el tipo de lista y procesar items
    let htmlTag, items;

    if (style === 'checklist') {
      htmlTag = 'ul';
      items = processItems(block.data.items || [], true);
    } else {
      htmlTag = style === 'ordered' ? 'ol' : 'ul';
      items = processItems(block.data.items || [], false);
    }

    // Aplicar alineación si está especificada en tunes
    const alignment = block.tunes?.alignment?.alignment;
    const alignClass = alignment && alignment !== 'left' ? ` class="text-${alignment}"` : '';

    // Para checklist, agregar clase específica
    const checklistClass = style === 'checklist' ? ' checklist' : '';
    const finalClass = alignClass || checklistClass ? ` class="${(alignClass.replace(' class="', '').replace('"', '') + checklistClass).trim()}"` : '';

    return `<${htmlTag}${finalClass}>${items}</${htmlTag}>`;
  },
  quote: (block: any) => {
    return `<blockquote><p>${block.data.text || ''}</p><cite>${block.data.caption || ''}</cite></blockquote>`;
  },
  delimiter: () => {
    return '<hr>';
  },
  table: (block: any) => {
    const rows = block.data.content.map((row: string[]) => {
      const cells = row.map(cell => `<td>${cell}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    return `<table><tbody>${rows}</tbody></table>`;
  }
});

/**
 * Convierte datos JSON de Editor.js a HTML
 * @param data - Datos de salida de Editor.js
 * @returns String HTML
 */
export const convertEditorJSONToHTML = (data: OutputData): string => {
  try {
    const edjsParser = edjsHTML(getCustomParsers());
    const htmlResult = edjsParser.parse(data);
    return Array.isArray(htmlResult) ? htmlResult.join('') : htmlResult;
  } catch (error) {
    console.error('Error converting Editor.js JSON to HTML:', error);
    return '';
  }
};

/**
 * Convierte HTML a formato JSON de Editor.js
 * @param html - String HTML
 * @returns Datos en formato Editor.js
 */
export const convertHTMLToEditorJSON = (html: string): OutputData => {
  try {
    if (!html || html.trim() === '') {
      return {
        time: Date.now(),
        blocks: [],
        version: '2.30.8'
      };
    }

    // Crear un elemento temporal para parsear el HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const blocks: any[] = [];
    let blockId = 0;

    // Función para generar ID único
    const generateId = () => `block_${blockId++}_${Date.now()}`;

    // Procesar cada elemento hijo
    Array.from(tempDiv.children).forEach((element) => {
      const tagName = element.tagName.toLowerCase();
      const textContent = element.textContent || '';
      const innerHTML = element.innerHTML || '';

      switch (tagName) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          // Verificar si es un título personalizado (title tool)
          const titleType = tagName.toUpperCase();
          const hasCustomStyling = element.hasAttribute('style') || element.hasAttribute('class');

          if (hasCustomStyling) {
            blocks.push({
              id: generateId(),
              type: 'title',
              data: {
                text: textContent,
                titleType: titleType,
                color: element.getAttribute('style')?.includes('color') ?
                  element.getAttribute('style')?.match(/color:\s*([^;]+)/)?.[1] : undefined,
                alignText: element.className || undefined
              }
            });
          } else {
            // Header estándar
            blocks.push({
              id: generateId(),
              type: 'header',
              data: {
                text: textContent,
                level: parseInt(tagName.charAt(1))
              }
            });
          }
          break;

        case 'p':
          if (textContent.trim()) {
            blocks.push({
              id: generateId(),
              type: 'paragraph',
              data: {
                text: innerHTML
              }
            });
          }
          break;

        case 'ul':
        case 'ol':
          // Verificar si es un checklist (ul con checkboxes)
          const hasCheckboxes = element.querySelector('li input[type="checkbox"]') !== null;

          if (hasCheckboxes && tagName === 'ul') {
            // Es un checklist
            const checklistItems = Array.from(element.querySelectorAll('li')).map(li => {
              const checkbox = li.querySelector('input[type="checkbox"]');
              const content = li.textContent?.replace(/^\s*/, '') || ''; // Remover espacios iniciales

              return {
                content: content,
                meta: {
                  checked: checkbox?.hasAttribute('checked') || false
                },
                items: []
              };
            });

            blocks.push({
              id: generateId(),
              type: 'list',
              data: {
                style: 'checklist',
                meta: {},
                items: checklistItems
              },
              tunes: {
                alignment: {
                  alignment: 'left'
                }
              }
            });
          } else {
            // Lista normal (ordered/unordered)
            const listItems = Array.from(element.querySelectorAll('li')).map(li => {
              const content = li.textContent || '';
              // Verificar si es formato legacy (string) o nuevo formato (objeto)
              return {
                content: content,
                items: []
              };
            });

            blocks.push({
              id: generateId(),
              type: 'list',
              data: {
                style: tagName === 'ol' ? 'ordered' : 'unordered',
                items: listItems
              }
            });
          }
          break;

        case 'blockquote':
          const quoteText = element.querySelector('p')?.textContent || textContent;
          const quoteCaption = element.querySelector('cite')?.textContent || '';
          blocks.push({
            id: generateId(),
            type: 'quote',
            data: {
              text: quoteText,
              caption: quoteCaption
            }
          });
          break;

        case 'hr':
          blocks.push({
            id: generateId(),
            type: 'delimiter',
            data: {}
          });
          break;

        case 'table':
          const rows = Array.from(element.querySelectorAll('tr')).map(row =>
            Array.from(row.querySelectorAll('td, th')).map(cell => cell.textContent || '')
          );
          blocks.push({
            id: generateId(),
            type: 'table',
            data: {
              content: rows
            }
          });
          break;

        default:
          // Para elementos no reconocidos, crear un párrafo
          if (textContent.trim()) {
            blocks.push({
              id: generateId(),
              type: 'paragraph',
              data: {
                text: innerHTML || textContent
              }
            });
          }
          break;
      }
    });

    // Si no hay bloques y hay contenido, crear un párrafo por defecto
    if (blocks.length === 0 && html.trim()) {
      blocks.push({
        id: generateId(),
        type: 'paragraph',
        data: {
          text: html
        }
      });
    }

    return {
      time: Date.now(),
      blocks,
      version: '2.30.8'
    };
  } catch (error) {
    console.error('Error converting HTML to Editor.js JSON:', error);
    return {
      time: Date.now(),
      blocks: [],
      version: '2.30.8'
    };
  }
};

/**
 * Valida si un string es HTML válido
 * @param str - String a validar
 * @returns boolean
 */
export const isValidHTML = (str: string): boolean => {
  try {
    const doc = new DOMParser().parseFromString(str, 'text/html');
    return doc.body.children.length > 0 || doc.body.textContent?.trim() !== '';
  } catch {
    return false;
  }
};

/**
 * Limpia HTML de elementos no deseados
 * @param html - HTML a limpiar
 * @returns HTML limpio
 */
export const sanitizeHTML = (html: string): string => {
  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Remover scripts y otros elementos peligrosos
    const dangerousElements = tempDiv.querySelectorAll('script, style, iframe, object, embed');
    dangerousElements.forEach(el => el.remove());

    return tempDiv.innerHTML;
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    return html;
  }
};